// Copyright 2026, Auxilio Technologies LLC

import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import { useShopStore } from '../store/shopStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8006/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    const language = localStorage.getItem('i18nextLng') || 'en';
    config.headers['Accept-Language'] = language;
    // Scope requests to the active shop (multi-shop). Cross-shop driver endpoints ignore it.
    const shopId = useShopStore.getState().activeShopId;
    if (shopId) config.headers['X-Shop-Id'] = shopId;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Don't run the refresh/redirect dance for the login or refresh calls themselves —
    // a failed login should surface its error inline, not bounce the user to '/'.
    const url: string = originalRequest?.url || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      const { refreshToken, updateTokens, logout } = useAuthStore.getState();
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const { access_token, refresh_token } = response.data;
          updateTokens(access_token, refresh_token);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          logout();
          window.location.href = '/';
          return Promise.reject(refreshError);
        }
      } else {
        logout();
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  register: (data: { email: string; username: string; password: string; role?: string; invitation_token?: string }) =>
    api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  verifyEmail: (email: string, code: string) => api.post('/auth/verify-email', { email, code }),
  resendVerificationEmail: (email: string) => api.post('/auth/resend-verification-email', { email }),
  listUsers: () => api.get('/auth/users'),
  updateUser: (userId: string, data: { role?: string; is_active?: boolean }) =>
    api.patch(`/auth/users/${userId}`, data),
  deleteUser: (userId: string) => api.delete(`/auth/users/${userId}`),
  inviteUser: (data: { email: string; role: string }) => api.post('/auth/invite', data),
};

export interface Order {
  id: string;
  order_number?: number;
  source: string;
  status: string;
  recipient_name: string;
  recipient_phone?: string;
  delivery_address: string;
  delivery_date: string;
  delivery_time_slot: string;
  arrangement_type?: string;
  special_notes?: string;
  card_message?: string;
  order_value_cents?: number;
  payment_status?: string;
  notify_mode: string;
  created_at?: string;
}

export const ordersAPI = {
  list: (params?: Record<string, any>) => api.get('/orders', { params }),
  get: (id: string) => api.get(`/orders/${id}`),
  create: (data: Record<string, any>) => api.post('/orders', data),
  update: (id: string, data: Record<string, any>) => api.patch(`/orders/${id}`, data),
  changeStatus: (id: string, status: string, reason?: string, override = false) =>
    api.patch(`/orders/${id}/status`, { status, reason, override }),
  remove: (id: string) => api.delete(`/orders/${id}`),
};

export const prepAPI = {
  queue: (params?: Record<string, any>) => api.get('/prep-queue', { params }),
  markReady: (id: string) => api.post(`/prep-queue/${id}/ready`),
  start: (id: string) => api.post(`/prep-queue/${id}/start`),
};

export interface RouteStop {
  id: string;
  run_id: string;
  order_id: string;
  sequence: number;
  status: string;
  outcome?: string;
  outcome_note?: string;
  order?: Order;
}

export interface DispatchRun {
  id: string;
  run_number?: number;
  time_slot: string;
  run_date: string;
  driver_user_id?: string;
  status: string;
  stops: RouteStop[];
  shop_name?: string;
  shop_address?: string;
}

export const shopsAPI = {
  list: () => api.get('/shops'),
  create: (data: { name: string; address?: string; phone?: string; timezone?: string }) =>
    api.post('/shops', data),
  update: (id: string, data: { name?: string; address?: string; phone?: string; timezone?: string }) =>
    api.patch(`/shops/${id}`, data),
};

export const runsAPI = {
  drivers: () => api.get('/runs/drivers'),
  readyOrders: (date: string, timeSlot?: string) =>
    api.get('/runs/ready-orders', { params: { date, time_slot: timeSlot } }),
  list: (date: string, timeSlot?: string) => api.get('/runs', { params: { date, time_slot: timeSlot } }),
  create: (data: { time_slot: string; run_date: string; driver_user_id?: string | null }) =>
    api.post('/runs', data),
  addStops: (runId: string, orderIds: string[]) => api.post(`/runs/${runId}/stops`, { order_ids: orderIds }),
  removeStop: (runId: string, stopId: string) => api.delete(`/runs/${runId}/stops/${stopId}`),
  reorder: (runId: string, stopIds: string[]) => api.patch(`/runs/${runId}/reorder`, { stop_ids: stopIds }),
  assign: (runId: string, driverId: string) => api.patch(`/runs/${runId}/assign`, { driver_user_id: driverId }),
  dispatch: (runId: string) => api.post(`/runs/${runId}/dispatch`),
  // Super admin may pass driverId to preview another driver's deliveries.
  mine: (driverId?: string) => api.get('/runs/driver/mine', { params: driverId ? { driver_id: driverId } : {} }),
};

export const stopsAPI = {
  outcome: (stopId: string, outcome: string, note?: string) =>
    api.post(`/stops/${stopId}/outcome`, { outcome, note }),
};

export interface DashboardSummary {
  total: number;
  pipeline: Record<string, number>;
  outcomes: Record<string, number>;
}

export const dashboardAPI = {
  summary: (dateFrom?: string, dateTo?: string) =>
    api.get('/dashboard/summary', { params: { date_from: dateFrom, date_to: dateTo } }),
};

export default api;
